// Central legal entity configuration for ApplyDrive
// Update this single file if company details change (registered office, ICO number, etc.)

export const LEGAL_ENTITY = {
  tradingName: 'ApplyDrive',
  companyName: 'Phase23 Ltd',
  companyNumber: '17247792',
  registeredOffice: '66 Paul Street, London, England, EC2A 4NA',
  jurisdiction: 'England and Wales',
  icoNumber: 'ZC161033',
  privacyEmail: 'privacy@applydrive.uk',
  companiesHouseUrl: 'https://find-and-update.company-information.service.gov.uk/company/17247792',
  icoRegisterUrl: 'https://ico.org.uk/ESDWebPages/Entry/ZC161033',
};

// One-line legal disclosure used in footers across the site
export const LEGAL_DISCLOSURE_LINE =
  `${LEGAL_ENTITY.tradingName} is a trading name of ${LEGAL_ENTITY.companyName}, ` +
  `a company registered in ${LEGAL_ENTITY.jurisdiction} (Company No. ${LEGAL_ENTITY.companyNumber}). ` +
  `Registered office: ${LEGAL_ENTITY.registeredOffice}. ICO Registration: ${LEGAL_ENTITY.icoNumber}.`;

// Sub-processors used to deliver the service.
// Listed for transparency in line with UK GDPR Art. 28 disclosure requirements.
//
// IMPORTANT: This shared list is shown on BOTH the generic ApplyDrive privacy/platform
// notice pages AND the dealer-branded ones. Specific broker submission targets
// (AutoConvert, Jigsaw, etc.) are intentionally separated into BROKER_SUB_PROCESSORS_NAMED
// below — they are shown ONLY on dealer-branded source pages, where naming the broker
// is transparent and appropriate. On the generic ApplyDrive pages we instead show
// BROKER_SUB_PROCESSORS_GENERIC, which keeps our commercial partners confidential.
export const SUB_PROCESSORS = [
  {
    name: 'Amazon Web Services (AWS)',
    role: 'Cloud hosting infrastructure (EC2, networking)',
    dataResidency: 'United Kingdom (eu-west-2 region)',
  },
  {
    name: 'MongoDB Atlas',
    role: 'Database storage for anonymous metadata only (no applicant PII)',
    dataResidency: 'United Kingdom / EEA',
  },
  {
    name: 'Google LLC',
    role: 'AI-powered text extraction from uploaded documents',
    dataResidency: 'United States (under UK adequacy decision + SCCs)',
  },
  {
    name: 'Resend',
    role: 'Transactional email delivery for submission notifications',
    dataResidency: 'European Union (eu-west-1, Ireland)',
  },
  {
    name: 'Ideal Postcodes',
    role: 'UK address lookup and validation (postcode-only data sent)',
    dataResidency: 'United Kingdom',
  },
  {
    name: 'DVLA Vehicle Enquiry Service',
    role: 'Vehicle details lookup by registration number',
    dataResidency: 'United Kingdom',
  },
];

// Shown ONLY on dealer-branded /s/{slug}/privacy pages — names the actual broker partner.
// Keyed by the source's broker_system value so each dealer's privacy notice names only the
// broker their applications actually flow to (avoids listing competitor platforms).
const BROKER_SUB_PROCESSORS_BY_SYSTEM = {
  autoconvert: {
    name: 'AutoConvert',
    role: 'Finance proposal submission (used per dealer configuration)',
    dataResidency: 'United Kingdom',
  },
  artis: {
    name: 'Jigsaw Finance Ltd (Artis API)',
    role: 'Finance proposal submission (used per dealer configuration)',
    dataResidency: 'United Kingdom',
  },
};

// Shown when a source submits applications via PDF email rather than a broker API —
// no third-party broker processor involved beyond Resend (already in SUB_PROCESSORS).
const BROKER_SUB_PROCESSOR_PDF_EMAIL = {
  name: 'Email delivery to dealer',
  role: 'Application PDF emailed directly to the dealer\'s nominated address',
  dataResidency: 'United Kingdom',
};

// Shown on the generic ApplyDrive privacy/platform pages — keeps partner identities confidential
// while still disclosing that a broker submission processor exists, satisfying GDPR Art. 28.
export const BROKER_SUB_PROCESSORS_GENERIC = [
  {
    name: 'FCA-authorised broker submission platforms',
    role: 'Finance proposal submission to dealer\'s chosen broker (specific platform varies per dealer configuration; disclosed in the dealer-branded privacy notice)',
    dataResidency: 'United Kingdom',
  },
];

// Helper: returns the full sub-processor list, with the right broker tier appended.
//   - No source             → generic (hides commercial partners)
//   - Source + PDF email    → "Email delivery to dealer"
//   - Source + API + system → only the named broker that source actually uses
export const getSubProcessors = (source) => {
  if (!source) {
    return [...SUB_PROCESSORS, ...BROKER_SUB_PROCESSORS_GENERIC];
  }
  if (source.submission_mode === 'pdf_email') {
    return [...SUB_PROCESSORS, BROKER_SUB_PROCESSOR_PDF_EMAIL];
  }
  const brokerKey = (source.broker_system || 'autoconvert').toLowerCase();
  const broker = BROKER_SUB_PROCESSORS_BY_SYSTEM[brokerKey] || BROKER_SUB_PROCESSORS_BY_SYSTEM.autoconvert;
  return [...SUB_PROCESSORS, broker];
};
